import { NetCode } from '@/graphql/generated/types.generated';

/**
 * Networks that must never get their own card in a connect picker.
 *
 * INSTAGRAM and INSTAGRAM_V2 are two routes into the same network, not two networks. INSTAGRAM_V2
 * signs in to Instagram directly and brings in the one account whose login the person has;
 * INSTAGRAM signs in through Facebook and brings in every account they administer, which is how
 * agencies reach client accounts they have no password for.
 *
 * Showing both cards asks the user to choose an API, which nobody can do. The single INSTAGRAM_V2
 * card asks the question instead, in InstagramRouteDialog, and starts whichever route the answer
 * implies. Re-authorising an existing profile is unaffected: those call sites pass the net code of
 * the profile they already have, they do not read this list.
 */
const HIDDEN_NET_CODES: readonly NetCode[] = [NetCode.Instagram];

export const isHiddenNet = (code: NetCode): boolean => HIDDEN_NET_CODES.includes(code);

/**
 * Drops the networks that should not appear as their own card. Use this on every list of nets
 * rendered as a picker, so the surfaces cannot drift apart.
 */
export function visibleNets<T extends { code: NetCode }>(nets: readonly T[] | undefined): T[] {
    return (nets ?? []).filter((net) => !isHiddenNet(net.code));
}

/**
 * Every net code a single card stands for. Only the Instagram card covers more than one, because
 * it hides the second route rather than dropping it.
 *
 * Use this, never `net.code`, when deciding whether a card is already connected: a profile
 * connected through Facebook carries INSTAGRAM, so matching on the card's own INSTAGRAM_V2 would
 * report "not connected" to someone who is.
 */
export function netCodesForCard(code: NetCode): NetCode[] {
    return code === NetCode.InstagramV2 ? [NetCode.InstagramV2, NetCode.Instagram] : [code];
}

/**
 * What a network card should be called, which is not always what the API calls it.
 *
 * The API names the two Instagram routes "Instagram" and "Instagram Business". "Business" is Meta's
 * word for an account setting, not for a way of signing in, and users read it as a different product
 * rather than a different door to the same one. Wherever both appear side by side, name them by the
 * login they open.
 *
 * Left untranslated on purpose: these are brand names plus "via", which reads the same in every
 * language we ship.
 */
export function netDisplayName(code: NetCode, apiName: string): string {
    if (code === NetCode.InstagramV2) return 'Instagram';
    if (code === NetCode.Instagram) return 'Instagram via Facebook';
    return apiName;
}
